import * as React from 'react'
import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { listClients, resendClientInvite, type Client } from '@/features/admin/api'
import { AddOrganizationDrawer } from './AddOrganizationDrawer'
import { EditOrganizationDrawer } from './EditOrganizationDrawer'
import { AdminSidebar } from '@/components/layout/AdminSidebar'
import { Button } from '@/components/primitives/Button'
import { Pagination } from '@/components/primitives/Pagination'
import { PageLoader } from '@/components/feedback/PageLoader'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import '../ManagePages.css'

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function orgInitials(name: string): string {
  return name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2).toUpperCase()
}

const AVATAR_COLORS = ['#4f6ef7', '#0ea5a0', '#7c3aed', '#d97706', '#db2777', '#059669', '#0284c7']
function avatarColor(name: string): string {
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) ?? 0)) % AVATAR_COLORS.length
  return AVATAR_COLORS[idx] ?? '#4f6ef7'
}

export function AdminOrganizationsPage(): React.JSX.Element {
  const toast = useToast()
  const [addOpen, setAddOpen] = useState(false)
  const [editClient, setEditClient] = useState<Client | null>(null)

  const orgsQuery = useQuery({ queryKey: ['admin-orgs'], queryFn: listClients })

  const resend = useMutation({
    mutationFn: (c: Client) => resendClientInvite(c.id),
    onSuccess: (res) => {
      if (!res.email_sent) toast.warning('Resend failed', 'Check SES config.')
      else if (res.invite_url) toast.success('Invite resent', `Dev link: ${res.invite_url}`)
      else toast.success('Invite resent', `Sent to ${res.invite_email}.`)
    },
    onError: (err) =>
      toast.error('Resend failed', err instanceof ApiError ? err.message : 'Try again.'),
  })

  const allOrgs = orgsQuery.data ?? []
  const [search, setSearch]   = useState('')
  const [page, setPage]       = useState(1)
  const [perPage, setPerPage] = useState(25)

  const orgs = useMemo(() => {
    let r = [...allOrgs]
    if (search.trim()) {
      const q = search.toLowerCase()
      r = r.filter(o =>
        o.name.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q) ||
        (o.primary_contact_name ?? '').toLowerCase().includes(q) ||
        (o.primary_contact_email ?? '').toLowerCase().includes(q),
      )
    }
    r.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0
      const db = b.created_at ? new Date(b.created_at).getTime() : 0
      return db - da
    })
    return r
  }, [allOrgs, search])

  useEffect(() => { setPage(1) }, [search])

  const totalPages = Math.max(1, Math.ceil(orgs.length / perPage))
  const pageOrgs   = orgs.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-shell-content">
      <AddOrganizationDrawer open={addOpen} onClose={() => setAddOpen(false)} />
      <EditOrganizationDrawer client={editClient} onClose={() => setEditClient(null)} />
      <main className="manage-main">
        <div className="manage-head-row">
          <div>
            <h1 className="manage-title">Organizations</h1>
            <p className="manage-sub">All LTC operators on the platform.</p>
          </div>
          <Button onClick={() => setAddOpen(true)} leftIcon={<PlusIcon />}>Add organization</Button>
        </div>

<div className="manage-card-table">
          <div className="manage-card-table-header">
            <div>
              <div className="manage-card-table-title">All organizations</div>
              {allOrgs.length > 0 && (
                <div className="manage-card-table-sub">{orgs.length} of {allOrgs.length} organization{allOrgs.length !== 1 ? 's' : ''}</div>
              )}
            </div>
            <div className="manage-toolbar">
              <input
                className="manage-search"
                placeholder="Search by name or contact…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>

          {orgsQuery.isLoading ? (
            <div style={{ padding: 'var(--space-8)' }}><PageLoader /></div>
          ) : allOrgs.length === 0 ? (
            <div className="manage-empty-state">
              <OrgIcon />
              <p>No organizations yet.</p>
              <Button onClick={() => setAddOpen(true)}>Add your first</Button>
            </div>
          ) : orgs.length === 0 ? (
            <div className="manage-empty-state"><p>No results match your search.</p></div>
          ) : (
            <table className="manage-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Primary contact</th>
                  <th>Onboarded</th>
                  <th className="manage-col-action">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageOrgs.map((c) => {
                  const color = avatarColor(c.name)
                  return (
                    <tr key={c.id}>
                      <td>
                        <div className="admin-cell-with-avatar">
                          <div className="admin-avatar" style={{ background: color }}>
                            {orgInitials(c.name)}
                          </div>
                          <div>
                            <div className="manage-name">{c.name}</div>
                            <div className="manage-email mono">{c.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        {c.primary_contact_name || c.primary_contact_email ? (
                          <div>
                            {c.primary_contact_name && (
                              <div className="manage-name">{c.primary_contact_name}</div>
                            )}
                            {c.primary_contact_email && (
                              <div className="manage-email">{c.primary_contact_email}</div>
                            )}
                          </div>
                        ) : (
                          <span className="manage-email">—</span>
                        )}
                        {c.pending_invite && (
                          <span className="status-badge status-badge--invited" style={{ marginTop: 4, display: 'inline-flex' }}>
                            Invite pending
                          </span>
                        )}
                      </td>
                      <td><span className="manage-email">{fmtDate(c.created_at)}</span></td>
                      <td className="manage-col-action">
                        <div className="manage-row-actions">
                          <Button variant="ghost" size="sm" leftIcon={<PencilIcon />} onClick={() => setEditClient(c)}>
                            Edit
                          </Button>
                          {c.pending_invite && (
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={resend.isPending && resend.variables?.id === c.id}
                              onClick={() => resend.mutate(c)}
                            >
                              Resend
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          <Pagination page={page} totalPages={totalPages} total={orgs.length} perPage={perPage} onPage={setPage} onPerPage={n => { setPerPage(n); setPage(1) }} />
        </div>
      </main>
      </div>
    </div>
  )
}

function PlusIcon(): React.JSX.Element {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
}
function PencilIcon(): React.JSX.Element {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
}
function OrgIcon(): React.JSX.Element {
  return <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
}
